import React, { useEffect, useState } from 'react';
import { db } from '../db';
import ReactDOM from 'react-dom';
import { useLogin } from '../hooks/useLogin';
import PasswordHidden from '../images/icons/input-bar-icons/icon-input-bar-password-hidden.svg';
import PasswordShown from '../images/icons/input-bar-icons/icon-input-bar-password-shown.svg';
import CloseIcon from "../images/icons/white-close.svg";
import GoogleIcon from '../images/icons/icon-auth-google.svg';
import ChevronRight from '../images/icons/chevron-right.svg';

/**
 * LoginModal Component
 *
 * A modal overlay that displays a login form matching the design from Profile.jsx
 * Used when users need to log in to continue with an action (e.g., generating)
 *
 * Props:
 * - isOpen: boolean - Whether the modal is visible
 * - onClose: function - Called when modal should close
 * - onLoginSuccess: function - Called after successful login with user data
 */
export default function LoginModal({
    isOpen,
    onClose,
    onLoginSuccess
}) {
    const {
        emailAddress,
        setEmailAddress,
        password,
        setPassword,
        passwordVisibility,
        setPasswordVisibility,
        logloading,
        error,
        login,
        resetForm,
        register,
        registerEmail,
        setRegisterEmail,
        registerPassword,
        setRegisterPassword,
        registerConfirmPassword,
        setRegisterConfirmPassword,
        registerPasswordVisibility,
        setRegisterPasswordVisibility,
        registerLoading,
        registerError,
        handleGoogleSignIn,
        googleSignInLoading,
        apiKeyInput,
        setApiKeyInput,
        apiKeyEmailInput,
        setApiKeyEmailInput,
        apiKeyLoading,
        apiKeyError,
        apiKeyLogin,
        accountContext
    } = useLogin();

    const [showRegisterForm, setShowRegisterForm] = useState(false);
    const [showApiKeyForm, setShowApiKeyForm] = useState(false);
    const [apiKeyLoginEnabled, setApiKeyLoginEnabled] = useState(false);

    useEffect(() => {
        db.settings.get({ key: 'enable_api_key_login' }).then(setting => {
            if (setting?.value) setApiKeyLoginEnabled(true);
        }).catch(() => {});
    }, []);

    // Handle escape key
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    // Auto-close modal when user successfully logs in via any method (including Google)
    useEffect(() => {
        if (isOpen && accountContext.loggedIn) {
            console.log('[LoginModal] User logged in detected - auto-closing modal');
            handleClose();
        }
    }, [isOpen, accountContext.loggedIn]);

    // Handle enter key in form
    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.keyCode === 13) {
            if (showRegisterForm) {
                handleRegister();
            } else {
                handleLogin();
            }
        }
    };

    const handleClose = () => {
        resetForm();
        setShowRegisterForm(false);
        setShowApiKeyForm(false);
        onClose();
    };

    const handleApiKeyLogin = async () => {
        const success = await apiKeyLogin((userData) => {
            if (onLoginSuccess) {
                onLoginSuccess(userData);
            }
            handleClose();
        });
    };

    const handleRegister = async () => {
        const success = await register((userData) => {
            // Registration successful or verification needed
            // Switch to login form (the register function already prefills the form)
            setShowRegisterForm(false);

            // Don't close the modal - let user log in with prefilled credentials
            // if (onLoginSuccess) {
            //     onLoginSuccess(userData);
            // }
            // handleClose();
        });
    };

    const handleLogin = async () => {
        const success = await login((userData) => {
            // Login successful
            if (onLoginSuccess) {
                onLoginSuccess(userData);
            }
            handleClose();
        });
    };

    const handleBackdropClick = (e) => {
        // Only close if clicking on the backdrop itself
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    const handleContentClick = (e) => {
        // Prevent closing when clicking modal content
        e.stopPropagation();
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-dark-blue/40 backdrop-blur overflow-y-auto"
            onClick={handleBackdropClick}
            style={{ pointerEvents: 'auto' }}
        >
            <div
                className="w-full max-w-md mx-auto my-8"
                onClick={handleContentClick}
            >
                {/* Modal Header */}
                <div className="w-full bg-dark-blue flex flex-row place-items-center justify-between px-6 py-4 rounded-t-xl">
                    <div className="flex-grow">
                        <h2 className="text-xl font-bold text-white">{showRegisterForm ? 'Create Account' : showApiKeyForm ? 'API Key Login' : 'Login Required'}</h2>
                    </div>
                    <button
                        className="px-3 py-2 rounded-lg bg-dark-purple/60 text-white hover:bg-dark-purple hover:text-yellow transition-all"
                        onClick={handleClose}
                    >
                        <img src={CloseIcon} alt='Close' className='h-6 w-6'/>
                    </button>
                </div>

                {/* Modal Content */}
                <div className="bg-dark-blue rounded-b-xl flex flex-col p-5">
                    {/* Google Sign In Button - hidden in API Key mode */}
                    {!showApiKeyForm && (
                    <div className="flex flex-col">
                        <div className={"py-2 pt-0"}>
                            <button
                                className={`w-full rounded-lg bg-white shadow-lg p-4 my-5 text-gray-700 flex flex-row items-center justify-between ${googleSignInLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                onClick={handleGoogleSignIn}
                                disabled={googleSignInLoading}
                            >
                                <span>
                                    <img className="inline-block mr-2 h-6" src={GoogleIcon} alt="Google Icon"/>
                                    {googleSignInLoading ? 'Connecting to Google...' : `${showRegisterForm ? 'Register' : 'Log In'} with Google`}
                                </span>
                                {googleSignInLoading ? (
                                    <div className="animate-spin h-6 w-6 border-2 border-gray-500 rounded-full border-t-transparent"></div>
                                ) : (
                                    <img src={ChevronRight} className="h-6" alt="Arrow Right"/>
                                )}
                            </button>
                        </div>
                    </div>
                    )}

                    {/* Login/Register/API Key Forms */}
                    {showRegisterForm ? null : !showApiKeyForm ? (
                        /* Promptus Login Form */
                        <div className="flex flex-col">
                            <div className="py-2 pt-5">
                                <label className="block mb-2 text-sm font-medium text-white" htmlFor="emailAddress">
                                    Email Address
                                </label>
                                <input
                                    type="text"
                                    name="emailAddress"
                                    value={emailAddress}
                                    onChange={(e) => setEmailAddress(e.target.value)}
                                    className="w-full rounded-lg text-dark-blue border-0 p-2"
                                    placeholder="Email Address"
                                    onKeyDown={handleKeyDown}
                                />
                            </div>

                            <div className="py-2 relative">
                                <label className="block mb-2 text-sm font-medium text-white" htmlFor="password">
                                    Password
                                </label>
                                <input
                                    type={passwordVisibility ? "text" : "password"}
                                    name="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full rounded-lg text-dark-blue border-0 p-2 pr-10"
                                    placeholder="Password"
                                />
                                <button
                                    type="button"
                                    className="w-9 absolute inset-y-0 right-0 top-7 flex items-center justify-center text-dark-blue"
                                    onClick={() => setPasswordVisibility(!passwordVisibility)}
                                >
                                    <img
                                        src={passwordVisibility ? PasswordShown : PasswordHidden}
                                        alt={passwordVisibility ? "Hide password" : "Show password"}
                                        className="h-5"
                                    />
                                </button>
                            </div>

                            {/* Login Form Links */}
                            <div className="flex flex-row justify-end mt-2">
                                <span onClick={() => setShowRegisterForm(true)} className="font-semibold text-sm hover:text-yellow cursor-pointer text-white">Don't have an account? Register</span>
                            </div>

                            {/* Error Display */}
                            {error && (
                                <div className="w-3/4 mx-auto my-3 p-2 bg-red-100 border border-red-400 text-red-700 text-center font-semibold rounded">
                                    {error}
                                </div>
                            )}

                            {/* Login Button */}
                            <button
                                onClick={handleLogin}
                                className="rounded w-1/2 mx-auto mt-6 mb-3 p-2 bg-yellow text-dark-blue shadow font-bold disabled:opacity-50"
                                disabled={logloading}
                            >
                                {logloading ? (
                                    <svg
                                        aria-hidden="true"
                                        role="status"
                                        className="inline w-5 h-5 mr-3 mb-1 text-dark-blue animate-spin"
                                        viewBox="0 0 99 101"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                            fill="#ffb02e"
                                        />
                                        <path
                                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                ) : null}
                                Log In
                            </button>

                            {/* TODO: restore apiKeyLoginEnabled check after testing */}
                            <div className="flex items-center my-4">
                                <hr className="flex-grow border border-white/40"/>
                                <span className="px-3 text-sm text-white/60">OR</span>
                                <hr className="flex-grow border border-white/40"/>
                            </div>

                            <button
                                onClick={() => setShowApiKeyForm(true)}
                                className="text-sm font-semibold text-yellow hover:text-yellow/80 cursor-pointer text-center"
                            >
                                Login with API Key
                            </button>
                        </div>
                    ) : !showRegisterForm ? (
                        /* API Key Login Form */
                        <div className="flex flex-col">
                            <div className="py-2 pt-5">
                                <label className="block mb-2 text-sm font-medium text-white" htmlFor="modalApiKeyEmailInput">Email Address</label>
                                <input
                                    type="email"
                                    name="modalApiKeyEmailInput"
                                    value={apiKeyEmailInput}
                                    onChange={(e) => setApiKeyEmailInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleApiKeyLogin(); }}
                                    className="w-full rounded-lg text-dark-blue border-0 p-2"
                                    placeholder="Email Address"
                                />
                            </div>
                            <div className="py-2">
                                <label className="block mb-2 text-sm font-medium text-white" htmlFor="modalApiKeyInput">API Key</label>
                                <input
                                    type="password"
                                    name="modalApiKeyInput"
                                    value={apiKeyInput}
                                    onChange={(e) => setApiKeyInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleApiKeyLogin(); }}
                                    className="w-full rounded-lg text-dark-blue border-0 p-2"
                                    placeholder="API Key"
                                />
                            </div>

                            {apiKeyError && (
                                <div className="w-3/4 mx-auto my-3 p-2 bg-red-100 border border-red-400 text-red-700 text-center font-semibold rounded">
                                    {apiKeyError}
                                </div>
                            )}

                            <button
                                onClick={handleApiKeyLogin}
                                className="rounded w-1/2 mx-auto mt-6 mb-3 p-2 bg-yellow text-dark-blue shadow font-bold disabled:opacity-50"
                                disabled={apiKeyLoading || !apiKeyInput.trim() || !apiKeyEmailInput.trim()}
                            >
                                {apiKeyLoading ? (
                                    <svg aria-hidden="true" role="status" className="inline w-5 h-5 mr-3 mb-1 text-dark-blue animate-spin" viewBox="0 0 99 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#ffb02e"/>
                                        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
                                    </svg>
                                ) : null}
                                Verify & Login
                            </button>

                            <div className="flex items-center my-4">
                                <hr className="flex-grow border border-white/40"/>
                                <span className="px-3 text-sm text-white/60">OR</span>
                                <hr className="flex-grow border border-white/40"/>
                            </div>

                            <button
                                onClick={() => setShowApiKeyForm(false)}
                                className="text-sm font-semibold text-yellow hover:text-yellow/80 cursor-pointer text-center"
                            >
                                Login with Promptus
                            </button>
                        </div>
                    ) : (
                        /* Register Form */
                        <div className="flex flex-col">
                            <div className={"py-2"}>
                                <label className="block mb-2 text-sm font-medium text-white" htmlFor="registerEmail">Email Address</label>
                                <input
                                    type="email"
                                    name="registerEmail"
                                    value={registerEmail}
                                    onChange={(e) => setRegisterEmail(e.target.value)}
                                    className="w-full rounded-lg text-dark-blue border-0 p-2"
                                    placeholder="Email Address"
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                            <div className={"py-2 relative"}>
                                <label className="block mb-2 text-sm font-medium text-white" htmlFor="registerPassword">Password</label>
                                <input
                                    type={registerPasswordVisibility ? "text" : "password"}
                                    name="registerPassword"
                                    value={registerPassword}
                                    onChange={(e) => setRegisterPassword(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full rounded-lg text-dark-blue border-0 p-2 pr-10"
                                    placeholder="Password"
                                />
                                <button
                                    type="button"
                                    className="w-9 absolute inset-y-0 right-0 top-7 flex items-center justify-center text-dark-blue"
                                    onClick={() => setRegisterPasswordVisibility(!registerPasswordVisibility)}
                                >
                                    <img
                                        src={registerPasswordVisibility ? PasswordShown : PasswordHidden}
                                        alt={registerPasswordVisibility ? "Hide password" : "Show password"}
                                        className="h-5"
                                    />
                                </button>
                            </div>
                            <div className={"py-2"}>
                                <label className="block mb-2 text-sm font-medium text-white" htmlFor="registerConfirmPassword">Confirm Password</label>
                                <input
                                    type="password"
                                    name="registerConfirmPassword"
                                    value={registerConfirmPassword}
                                    onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full rounded-lg text-dark-blue border-0 p-2"
                                    placeholder="Confirm Password"
                                />
                            </div>
                            <div className="flex flex-row justify-end mt-2">
                                <span onClick={() => setShowRegisterForm(false)} className="font-semibold text-sm hover:text-yellow cursor-pointer text-white">Already have an account? Log in</span>
                            </div>
                            {registerError && (
                                <div className="w-3/4 mx-auto my-3 p-2 bg-red-100 border border-red-400 text-red-700 text-center font-semibold rounded">
                                    {registerError}
                                </div>
                            )}
                            <div className="flex flex-row justify-between mt-4">
                                <button
                                    onClick={() => setShowRegisterForm(false)}
                                    className="rounded w-1/3 mx-auto p-2 bg-gray-500 text-white shadow font-bold"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleRegister}
                                    className="rounded w-1/3 mx-auto p-2 bg-yellow text-dark-blue shadow font-bold disabled:opacity-50"
                                    disabled={registerLoading}
                                >
                                    {registerLoading ? (
                                        <svg
                                            aria-hidden="true"
                                            role="status"
                                            className="inline w-5 h-5 mr-3 mb-1 text-dark-blue animate-spin"
                                            viewBox="0 0 99 101"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                fill="#ffb02e"
                                            />
                                            <path
                                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                    ) : null}
                                    Register
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.querySelector('.main-content') || document.body
    );
}